import { memo, useMemo } from 'react';

interface EmbedProps {
  url: string;
  title?: string;
}

// Extract YouTube video ID from various URL formats
function getYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

// Extract Vimeo video ID
function getVimeoId(url: string): string | null {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
}

// Extract Figma file/key from URL
function getFigmaEmbedUrl(url: string): string | null {
  const match = url.match(/figma\.com\/(file|board|slides|prototype)\/([a-zA-Z0-9]+)/);
  if (match) {
    return `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`;
  }
  return null;
}

// Extract CodeSandbox ID
function getCodeSandboxId(url: string): string | null {
  const match = url.match(/codesandbox\.io\/(?:s|embed)\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

// Extract GitHub Gist ID
function getGistInfo(url: string): { id: string; file?: string } | null {
  const match = url.match(/gist\.github\.com\/[^/]+\/([a-zA-Z0-9]+)(?:\?file=([^&]+))?/);
  if (match) {
    return { id: match[1], file: match[2] };
  }
  return null;
}

// Extract Loom video ID
function getLoomId(url: string): string | null {
  const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

// Check if URL is an embeddable content
export function isEmbeddableUrl(url: string): boolean {
  return !!(
    getYouTubeId(url) ||
    getVimeoId(url) ||
    getFigmaEmbedUrl(url) ||
    getCodeSandboxId(url) ||
    getGistInfo(url) ||
    getLoomId(url)
  );
}

export const Embed = memo(function Embed({ url, title }: EmbedProps) {
  const embedContent = useMemo(() => {
    // YouTube
    const youtubeId = getYouTubeId(url);
    if (youtubeId) {
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
        title: title || 'YouTube Video',
      };
    }

    // Vimeo
    const vimeoId = getVimeoId(url);
    if (vimeoId) {
      return {
        type: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
        title: title || 'Vimeo Video',
      };
    }

    // Figma
    const figmaUrl = getFigmaEmbedUrl(url);
    if (figmaUrl) {
      return {
        type: 'figma',
        embedUrl: figmaUrl,
        title: title || 'Figma Design',
      };
    }

    // CodeSandbox
    const sandboxId = getCodeSandboxId(url);
    if (sandboxId) {
      return {
        type: 'codesandbox',
        embedUrl: `https://codesandbox.io/embed/${sandboxId}?fontsize=14&hidenavigation=1&theme=dark`,
        title: title || 'CodeSandbox',
      };
    }

    // GitHub Gist
    const gistInfo = getGistInfo(url);
    if (gistInfo) {
      return {
        type: 'gist',
        embedUrl: `https://gist.github.com/${gistInfo.id}.pibb${gistInfo.file ? `?file=${gistInfo.file}` : ''}`,
        title: title || 'GitHub Gist',
      };
    }

    // Loom
    const loomId = getLoomId(url);
    if (loomId) {
      return {
        type: 'loom',
        embedUrl: `https://www.loom.com/embed/${loomId}`,
        title: title || 'Loom Video',
      };
    }

    return null;
  }, [url, title]);

  if (!embedContent) {
    // Not an embeddable URL, show as regular link
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline break-all"
      >
        {title || url}
      </a>
    );
  }

  const { type, embedUrl, title: embedTitle } = embedContent;

  // Video embeds (YouTube, Vimeo, Loom)
  if (['youtube', 'vimeo', 'loom'].includes(type)) {
    return (
      <div className="my-4 rounded-lg overflow-hidden border border-border">
        <div className="aspect-video">
          <iframe
            src={embedUrl}
            title={embedTitle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </div>
    );
  }

  // Figma embed
  if (type === 'figma') {
    return (
      <div className="my-4 rounded-lg overflow-hidden border border-border">
        <div className="bg-muted/50 px-4 py-2 text-sm text-muted-foreground border-b border-border flex items-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 38 57" fill="currentColor">
            <path d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z" />
            <path d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z" />
            <path d="M19 0V19H28.5C33.7467 19 38 14.7467 38 9.5C38 4.25329 33.7467 0 28.5 0H19Z" />
            <path d="M0 9.5C0 14.7467 4.25329 19 9.5 19H19V0H9.5C4.25329 0 0 4.25329 0 9.5Z" />
            <path d="M0 28.5C0 33.7467 4.25329 38 9.5 38H19V19H9.5C4.25329 19 0 23.2533 0 28.5Z" />
          </svg>
          Figma
        </div>
        <iframe
          src={embedUrl}
          title={embedTitle}
          className="w-full h-[450px]"
          allowFullScreen
        />
      </div>
    );
  }

  // CodeSandbox embed
  if (type === 'codesandbox') {
    return (
      <div className="my-4 rounded-lg overflow-hidden border border-border">
        <div className="bg-muted/50 px-4 py-2 text-sm text-muted-foreground border-b border-border flex items-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2 6l10-4 10 4-10 4-10-4zM2 18l10 4 10-4M2 12l10 4 10-4" stroke="currentColor" strokeWidth="2" fill="none" />
          </svg>
          CodeSandbox
        </div>
        <iframe
          src={embedUrl}
          title={embedTitle}
          className="w-full h-[500px]"
          allow="accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking"
          sandbox="allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
        />
      </div>
    );
  }

  // GitHub Gist embed
  if (type === 'gist') {
    return (
      <div className="my-4 rounded-lg overflow-hidden border border-border">
        <iframe
          src={embedUrl}
          title={embedTitle}
          className="w-full min-h-[200px]"
          style={{ border: 'none' }}
        />
      </div>
    );
  }

  return null;
});

// Embed placeholder component for loading/error states
export function EmbedPlaceholder({ type }: { type: string }) {
  return (
    <div className="my-4 p-4 bg-muted/50 rounded-lg border border-border flex items-center justify-center">
      <span className="text-muted-foreground">加载 {type} 内容中...</span>
    </div>
  );
}

// Supported embed types for documentation
export const SUPPORTED_EMBEDS = [
  { type: 'youtube', label: 'YouTube', pattern: 'youtube.com/watch, youtu.be' },
  { type: 'vimeo', label: 'Vimeo', pattern: 'vimeo.com' },
  { type: 'figma', label: 'Figma', pattern: 'figma.com/file, figma.com/board' },
  { type: 'codesandbox', label: 'CodeSandbox', pattern: 'codesandbox.io/s' },
  { type: 'gist', label: 'GitHub Gist', pattern: 'gist.github.com' },
  { type: 'loom', label: 'Loom', pattern: 'loom.com/share' },
] as const;
